import axios from 'axios';

const DEFAULT_SOURCE_ID = 'dd48a0088659468cb865ba390af6a873';

const getCurrentUTCTimestamp = () => new Date().toISOString();

const createRequestId = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  const randomBytes = new Uint8Array(16);
  globalThis.crypto?.getRandomValues(randomBytes);

  randomBytes[6] = (randomBytes[6] & 0x0f) | 0x40;
  randomBytes[8] = (randomBytes[8] & 0x3f) | 0x80;

  const hex = Array.from(randomBytes, (byte) => byte.toString(16).padStart(2, '0')).join('');

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
};

const base64ToUint8Array = (value: string) =>
  Uint8Array.from(atob(value), (char) => char.charCodeAt(0));

const bufferToHex = (buffer: ArrayBuffer) =>
  Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

const getHmacEncryptedData = async (timestamp: string, xRequestId: string) => {
  const secretKeyBase64 = process.env.REACT_APP_HMAC_SECRET_KEY;
  const subtleCrypto = globalThis.crypto?.subtle;

  if (!secretKeyBase64) {
    throw new Error('Missing REACT_APP_HMAC_SECRET_KEY.');
  }

  if (!subtleCrypto) {
    throw new Error('Web Crypto API is not available in this browser.');
  }

  const message = new TextEncoder().encode(`${xRequestId}:${xRequestId}:${timestamp}`);
  const secretKey = await subtleCrypto.importKey(
    'raw',
    base64ToUint8Array(secretKeyBase64),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const hmac = await subtleCrypto.sign('HMAC', secretKey, message);
  return bufferToHex(hmac);
};

const apiHmacService = axios.create({ timeout: 10000 });

apiHmacService.interceptors.request.use(async (config) => {
  const timestamp = getCurrentUTCTimestamp();
  const xRequestId = createRequestId();
  const hmacSignature = await getHmacEncryptedData(timestamp, xRequestId);
  const sourceId = process.env.REACT_APP_HMAC_SOURCE_ID || DEFAULT_SOURCE_ID;
  const headers = config.headers ?? {};

  headers['X-HMAC-Signature'] = hmacSignature;
  headers['X-Timestamp'] = timestamp;
  headers['X-Request-Id'] = xRequestId;
  headers['X-Device-Id'] = xRequestId;
  headers['Content-Type'] = 'application/json';
  headers['x-Source-Id'] = sourceId;

  config.headers = headers;
  return config;
});

export default apiHmacService;
